import * as THREE from "three";
import { cheapTextures } from "./mapTextures.js";

/**
 * Procedural SKY textures — the cloud field, the star field and the moon.
 *
 * Why (mission `graphics`, 2026-08-01): every entity family in the game has now
 * had a surface pass, and the sky was the last flat thing left — and it is the
 * single biggest object in almost every frame. It was a 512x256 canvas holding
 * a three-stop linear gradient, one radial-gradient "sun", and SIX hand-placed
 * ellipse blobs, stretched over a 560 m sphere. At that magnification a blob is
 * ~120 screen pixels of pure linear ramp: no silhouette, no internal structure,
 * no light direction, and — because the dome is camera-centred and the art is
 * baked into its UVs — no motion and no parallax. That is most of why outdoor
 * frames read as a toy diorama under a painted lid.
 *
 * These three textures are generated ONCE per tab (canvas, no asset files),
 * cached at module scope, and sampled by the sky dome's shader. Nothing here
 * runs per frame — the sky animates by advancing two uniform floats.
 *
 * Budget (full): 512² RGBA cloud + 1024x512 RGB stars + 128² RGBA moon
 * ≈ 3.3 MB pre-mip. `cheapTextures()` halves every dimension (≈0.85 MB).
 */

/** Deterministic LCG — a fixed pattern keeps A/B screenshots reproducible.
 *  (Client-side presentation only; invariant 7's determinism rule is about
 *  packages/shared and does not reach here.) */
function rnd(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function cvs(w: number, h: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  return { c, g: c.getContext("2d")! };
}

/** Value noise on an integer lattice that WRAPS at `period`, so every octave
 *  tiles and the assembled fBm tiles too. A non-tiling cloud texture is not an
 *  option here: the dome projects it across a plane that repeats to the
 *  horizon, and a seam would be a straight line across the sky. */
function periodicNoise(seed: number): (x: number, y: number, period: number) => number {
  const s0 = seed >>> 0;
  const hash = (ix: number, iy: number): number => {
    let h = (ix * 374761393 + iy * 668265263 + s0) >>> 0;
    h = (h ^ (h >>> 13)) >>> 0;
    h = Math.imul(h, 1274126177) >>> 0;
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
  };
  return (x, y, period) => {
    const ix = Math.floor(x), iy = Math.floor(y);
    const fx = x - ix, fy = y - iy;
    // smoothstep interpolation — bilinear alone leaves visible lattice creases
    const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
    const m = (v: number): number => ((v % period) + period) % period;
    const x0 = m(ix), y0 = m(iy), x1 = m(ix + 1), y1 = m(iy + 1);
    const top = hash(x0, y0) + (hash(x1, y0) - hash(x0, y0)) * ux;
    const bot = hash(x0, y1) + (hash(x1, y1) - hash(x0, y1)) * ux;
    return top + (bot - top) * uy;
  };
}

let cloudTex: THREE.CanvasTexture | null = null;

/**
 * The cloud field: RGB = a tangent-space NORMAL of the cloud-top height, A =
 * coverage. Tiles in both axes.
 *
 * Storing a normal (rather than a flat white blob) is what buys the sky a light
 * direction: the dome shader dots this against the SAME sun vector the scene's
 * directional light uses, so cloud tops facing the sun are hot white, the flanks
 * away from it go blue-grey, and the whole sky agrees with the shadows on the
 * ground. That is the cue the old ellipse blobs could never have.
 *
 * The height field is fBm run twice at different frequencies: a low-frequency
 * `mass` decides WHERE cumulus sit, a higher-frequency `erode` chews their
 * edges into wisps instead of leaving a soft-but-round balloon outline.
 */
export function cloudTexture(): THREE.CanvasTexture {
  if (cloudTex) return cloudTex;
  const N = cheapTextures() ? 256 : 512;
  const { c, g } = cvs(N, N);
  const img = g.createImageData(N, N);
  const px = img.data;
  const nz1 = periodicNoise(0x51c10d);
  const nz2 = periodicNoise(0x9ba7e1);

  // height field first (one float per texel), then differentiate it — computing
  // the normal from the same buffer keeps the lighting exactly consistent with
  // the coverage, and costs one extra Float32Array instead of 4 more fBm calls
  // per texel.
  const h = new Float32Array(N * N);
  const BASE = 3; // lattice cells across the tile at octave 0
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const u = x / N, v = y / N;
      let mass = 0, amp = 0.5, per = BASE;
      for (let o = 0; o < 5; o++) {
        mass += amp * nz1(u * per, v * per, per);
        amp *= 0.5; per *= 2;
      }
      let erode = 0; amp = 0.5; per = BASE * 6;
      for (let o = 0; o < 3; o++) {
        erode += amp * nz2(u * per, v * per, per);
        amp *= 0.5; per *= 2;
      }
      // cumulus: a soft threshold on the mass, then the erosion bites the rim
      // (full strength at the edge, nothing in the dense middle)
      // The threshold window is DELIBERATELY narrow. A wide one (the first
      // version used /0.30) leaves almost no fully opaque texels — measured by
      // agent/bench-sky.mjs at 1.9% opaque / 63.7% partial, i.e. a field of haze
      // rather than cumulus, and in the browser that reads as a milky sky with
      // no cloud bodies in it. fBm mass is centred near 0.48 with a modest
      // spread, so the window has to sit tight around that.
      const core = Math.max(0, Math.min(1, (mass - 0.455) / 0.115));
      const rim = 1 - Math.abs(core * 2 - 1); // 0 in the core, 0 outside, 1 at the edge
      h[y * N + x] = Math.max(0, Math.min(1, core - rim * (0.62 - erode) * 0.85));
    }
  }

  const at = (x: number, y: number): number => h[((y + N) % N) * N + ((x + N) % N)];
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const i = (y * N + x) * 4;
      const d = h[y * N + x];
      // central differences, scaled so a cloud edge reads as a steep shoulder
      const gx = (at(x - 1, y) - at(x + 1, y)) * 2.6;
      const gy = (at(x, y - 1) - at(x, y + 1)) * 2.6;
      const len = Math.hypot(gx, gy, 1);
      px[i] = ((gx / len) * 0.5 + 0.5) * 255;
      px[i + 1] = ((gy / len) * 0.5 + 0.5) * 255;
      px[i + 2] = ((1 / len) * 0.5 + 0.5) * 255;
      px[i + 3] = d * 255;
    }
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 8; // the plane projection is extremely oblique near the horizon
  t.userData.shared = true; // module-cached — teardown helpers must not dispose it
  cloudTex = t;
  return t;
}

let starTex: THREE.CanvasTexture | null = null;

/**
 * Equirectangular star field: a milky-way band plus ~2200 stars with a realistic
 * magnitude distribution (many faint, few bright) and B-V-ish colours.
 *
 * The old night sky drew 240 uniform-brightness dots straight onto the sky
 * canvas at 512x256, i.e. into the SAME texels as the gradient — so a star was
 * one blurred pixel smeared over ~2 m of dome. Here they live at 1024x512 with
 * no mipmaps (see below), sampled by direction, so they stay pin-sharp points.
 */
export function starTexture(): THREE.CanvasTexture {
  if (starTex) return starTex;
  const W = cheapTextures() ? 512 : 1024, H = W / 2;
  const { c, g } = cvs(W, H);
  g.fillStyle = "#000000";
  g.fillRect(0, 0, W, H);

  // MILKY WAY: a diagonal band of unresolved haze + dust lanes. Drawn as soft
  // additive strokes along a sine path so it is not a straight ruler line.
  const r = rnd(0x51a45);
  g.globalCompositeOperation = "lighter";
  for (let i = 0; i < 900; i++) {
    const t = r();
    const bx = t * W;
    const by = H * 0.42 + Math.sin(t * 5.4 + 0.8) * H * 0.16;
    const spread = H * (0.05 + r() * 0.09);
    const y = by + (r() + r() + r() - 1.5) * spread;
    const rad = 4 + r() * 16;
    const a = 0.012 + r() * 0.03;
    const grad = g.createRadialGradient(bx, y, 0, bx, y, rad);
    grad.addColorStop(0, `rgba(190,205,255,${a})`);
    grad.addColorStop(1, "rgba(190,205,255,0)");
    g.fillStyle = grad;
    g.fillRect(bx - rad, y - rad, rad * 2, rad * 2);
  }

  const COLORS = ["#ffffff", "#eaf0ff", "#cfe0ff", "#fff2d8", "#ffd9b0", "#ffc0a0", "#dfe8ff"];
  const count = cheapTextures() ? 1100 : 2200;
  for (let i = 0; i < count; i++) {
    const x = r() * W;
    // bias toward the milky-way band so the sky has structure, not confetti
    const near = r() < 0.42;
    const y = near
      ? Math.max(0, Math.min(H - 1, H * 0.42 + Math.sin((x / W) * 5.4 + 0.8) * H * 0.16 + (r() + r() - 1) * H * 0.14))
      : r() * H;
    // magnitude: cube of a uniform → many faint, a handful blazing
    const m = r() * r() * r();
    const rad = 0.35 + m * 2.1;
    const a = 0.28 + m * 0.72;
    const col = COLORS[Math.floor(r() * COLORS.length)];
    const grad = g.createRadialGradient(x, y, 0, x, y, rad * 2.6);
    grad.addColorStop(0, col);
    grad.addColorStop(0.28, `rgba(255,255,255,${a * 0.55})`);
    grad.addColorStop(1, "rgba(255,255,255,0)");
    g.globalAlpha = a;
    g.fillStyle = grad;
    g.fillRect(x - rad * 2.6, y - rad * 2.6, rad * 5.2, rad * 5.2);
    if (m > 0.55) { // the bright few get diffraction spikes
      g.strokeStyle = `rgba(255,255,255,${a * 0.35})`;
      g.lineWidth = 0.6;
      const L = rad * 4.5;
      g.beginPath(); g.moveTo(x - L, y); g.lineTo(x + L, y);
      g.moveTo(x, y - L); g.lineTo(x, y + L); g.stroke();
    }
  }
  g.globalAlpha = 1;
  g.globalCompositeOperation = "source-over";

  const t = new THREE.CanvasTexture(c);
  // NO mipmaps: the shader samples this by atan2(dir) and the U seam's screen-
  // space derivative explodes there, which with mips selects the 1x1 level and
  // paints a bright vertical stripe up the sky. Point-ish sampling of a star
  // field is also simply correct — mips average stars away to grey.
  t.generateMipmaps = false;
  t.minFilter = THREE.LinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.userData.shared = true;
  starTex = t;
  return t;
}

let moonTex: THREE.CanvasTexture | null = null;

/** The moon disc: limb-darkened body, dark maria, a few bright-rayed craters,
 *  soft alpha edge. Sampled in the dome's tangent frame around the moon
 *  direction, so it stays a round disc wherever it sits in the sky. */
export function moonTexture(): THREE.CanvasTexture {
  if (moonTex) return moonTex;
  const N = cheapTextures() ? 64 : 128;
  const { c, g } = cvs(N, N);
  const R = N * 0.5;
  const r = rnd(0x3f00d);
  g.clearRect(0, 0, N, N);
  // body with limb darkening
  const body = g.createRadialGradient(R * 0.82, R * 0.78, R * 0.1, R, R, R);
  body.addColorStop(0, "#fdf6ee");
  body.addColorStop(0.62, "#e8e0d6");
  body.addColorStop(0.9, "#bdb4ab");
  body.addColorStop(1, "#8f8880");
  g.save();
  g.beginPath(); g.arc(R, R, R * 0.97, 0, Math.PI * 2); g.clip();
  g.fillStyle = body;
  g.fillRect(0, 0, N, N);
  // maria — big dark basalt seas
  for (let i = 0; i < 7; i++) {
    const a = r() * Math.PI * 2, d = r() * R * 0.62;
    const x = R + Math.cos(a) * d, y = R + Math.sin(a) * d;
    const rr = R * (0.14 + r() * 0.22);
    const m = g.createRadialGradient(x, y, rr * 0.2, x, y, rr);
    m.addColorStop(0, "rgba(120,116,118,0.55)");
    m.addColorStop(1, "rgba(120,116,118,0)");
    g.fillStyle = m;
    g.beginPath(); g.ellipse(x, y, rr * 1.25, rr, r() * 3, 0, Math.PI * 2); g.fill();
  }
  // craters: a bright rim and a shaded floor
  for (let i = 0; i < 26; i++) {
    const a = r() * Math.PI * 2, d = Math.sqrt(r()) * R * 0.9;
    const x = R + Math.cos(a) * d, y = R + Math.sin(a) * d;
    const rr = R * (0.015 + r() * 0.055);
    g.fillStyle = "rgba(90,86,82,0.35)";
    g.beginPath(); g.arc(x, y, rr, 0, Math.PI * 2); g.fill();
    g.strokeStyle = "rgba(255,252,246,0.5)";
    g.lineWidth = Math.max(0.5, rr * 0.35);
    g.beginPath(); g.arc(x - rr * 0.12, y - rr * 0.12, rr * 0.92, 0, Math.PI * 2); g.stroke();
  }
  g.restore();
  // soft edge so the disc does not alias into a polygon at the limb
  const edge = g.createRadialGradient(R, R, R * 0.9, R, R, R);
  edge.addColorStop(0, "rgba(0,0,0,0)");
  edge.addColorStop(1, "rgba(0,0,0,1)");
  g.globalCompositeOperation = "destination-out";
  g.fillStyle = edge;
  g.fillRect(0, 0, N, N);
  g.globalCompositeOperation = "source-over";

  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  // NO mipmaps. The dome samples this inside an `if (md > 0.985)` branch, so the
  // 2x2 quads straddling that branch have a garbage UV derivative and pick an
  // arbitrary mip — in practice the 1x1 level, which is a uniform grey with
  // nonzero alpha and paints a dashed ring in the sky around the moon.
  t.generateMipmaps = false;
  t.minFilter = THREE.LinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.userData.shared = true;
  moonTex = t;
  return t;
}

/** Build all three under the load screen (called from renderer.precompile), so
 *  the first night-fall or first cloud does not generate a texture mid-fight. */
export function prewarmSkyTextures(): void {
  const t0 = performance.now();
  cloudTexture(); starTexture(); moonTexture();
  console.log(`[skytex] generated cloud + stars + moon in ${(performance.now() - t0).toFixed(0)} ms${cheapTextures() ? " (half-res — lowSpec)" : ""}`);
}
